<template>
    <p class="pb-6 text-base leading-6 font-medium text-black">Based on percentage split</p>

    <div v-for="(branch, index) in workflowStore.branches" :key="branch.label" class="flex items-center w-full gap-x-2">
        <div class="relative w-9/12 mb-5">
            <GgmsInput
                v-model.number="branch.value"
                :label="branch.label"
                type="number"
                min="0"
                max="100"
                @input="onPercentageChange(index)"
            />
            <span class="absolute inset-y-0 right-0 flex items-center pt-6 pr-2 text-gray-500">%</span>
        </div>
        <TrashIcon
            v-if="workflowStore.branches.length > 1"
            class="w-5 h-5 cursor-pointer"
            @click="removeBranch(index)"
        ></TrashIcon>
    </div>

    <GgmsButton
        styleType="simple"
        :icon-start="PlusIcon"
        class="w-fit"
        :classes="['py-0 px-0 pr-2 text-primary-color-600']"
        :icon-classes="['fill-primary-color-600']"
        @click="addBranch"
        >Add Branch</GgmsButton
    >
</template>
<script setup lang="ts">
import { onMounted } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import { PlusIcon, TrashIcon } from "@heroicons/vue/outline"
import { useWorkflowStore } from "@/stores/workflow"

const workflowStore = useWorkflowStore()

function addBranch(): void {
    const currentBranches = workflowStore.branches.length

    workflowStore.branches.push({
        value: 0,
        label: `Branch ${currentBranches + 1}`,
        type: "percentage",
    })

    redistributePercentages()
}

function removeBranch(index: number): void {
    workflowStore.branches.splice(index, 1)
    workflowStore.branches = workflowStore.branches.map((branch, idx) => ({
        ...branch,
        label: `Branch ${idx + 1}`,
    }))

    if (workflowStore.branches.length > 0) {
        redistributePercentages()
    }
}

function redistributePercentages(): void {
    const branchCount = workflowStore.branches.length
    if (branchCount === 0) return

    const equalPercentage = Math.floor(100 / branchCount)
    const remainder = 100 % branchCount

    workflowStore.branches.forEach((branch, index) => {
        branch.value = equalPercentage + (index < remainder ? 1 : 0)
        branch.type = "percentage"
    })
}

function onPercentageChange(index: number): void {
    const branch = workflowStore.branches[index]
    const numValue = Number(branch.value)

    // Ensure value is within bounds
    if (numValue < 0) {
        branch.value = 0
    } else if (numValue > 100) {
        branch.value = 100
    }
}

function adjustOtherBranches(changedIndex: number): void {
    const changedBranch = workflowStore.branches[changedIndex]
    const changedValue = Math.round(Number(changedBranch.value))
    changedBranch.value = changedValue // Ensure the changed value is also rounded

    const otherBranches = workflowStore.branches.filter((_, index) => index !== changedIndex)

    if (otherBranches.length === 0) return

    const remainingPercentage = 100 - changedValue
    const currentOtherTotal = otherBranches.reduce((sum, branch) => sum + Number(branch.value), 0)

    if (currentOtherTotal === 0 || remainingPercentage <= 0) {
        // Distribute remaining percentage equally among other branches
        const baseShare = Math.floor(Math.max(0, remainingPercentage) / otherBranches.length)
        const remainder = Math.max(0, remainingPercentage) % otherBranches.length

        otherBranches.forEach((branch, index) => {
            branch.value = baseShare + (index < remainder ? 1 : 0)
        })
    } else {
        // Adjust other branches proportionally
        const scaleFactor = remainingPercentage / currentOtherTotal
        let distributedTotal = 0

        otherBranches.forEach((branch, index) => {
            if (index === otherBranches.length - 1) {
                // Last branch gets whatever is left to ensure total is exactly 100
                branch.value = remainingPercentage - distributedTotal
            } else {
                branch.value = Math.round(Number(branch.value) * scaleFactor)
                distributedTotal += branch.value
            }
        })
    }
}

onMounted(() => {
    workflowStore.areButtonDisabled = false

    // Initialize with equal percentages if branches exist but have no values
    if (workflowStore.branches.length > 0) {
        const hasValidValues = workflowStore.branches.some((branch) => Number(branch.value) > 0)
        if (!hasValidValues) {
            redistributePercentages()
        }
    }
})
</script>
